import LoadingSpinner from "@/lib/components/LoadingSpinner";
import { useReferralOverviewQuery } from "@/lib/queries/referral-queries";
import { formatCreatedAt } from "@/lib/utils";
import { ReferralCreditIncreaseDto } from "@repo/api/referral";
import { IconAlertCircle, IconGift } from "@tabler/icons-react";
import { useParams } from "react-router";

export default function ReferralEarnedCreditsView() {
  const { teamExternalId } = useParams();
  const { data: referralOverview, status } = useReferralOverviewQuery({
    teamExternalId,
  });

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      {status === "success" && referralOverview.creditIncreases.length > 0 && (
        <EarnedCreditsList creditIncreases={referralOverview.creditIncreases} />
      )}
      {status === "success" &&
        referralOverview.creditIncreases.length === 0 && (
          <div className="flex flex-1 flex-col items-center justify-center self-stretch">
            <IconGift className="h-7 w-7 shrink-0 text-tertiary-400" />
            <p className="mt-3 font-semibold">
              Time to earn your first credits!
            </p>
            <p className="mb-7 text-sm text-text-secondary">
              Invite your friends & earn free credits
            </p>
          </div>
        )}
      {status === "error" && (
        <div className="flex flex-1 items-center justify-center self-stretch">
          <IconAlertCircle size={20} className="mr-1.5 text-error" />
          <p className="text-sm font-medium text-text-secondary">
            Failed to load earned credits
          </p>
        </div>
      )}
      {status === "pending" && (
        <div className="flex flex-1 items-center justify-center self-stretch">
          <LoadingSpinner size={24} />
        </div>
      )}
    </div>
  );
}

function EarnedCreditsList({
  creditIncreases,
}: {
  creditIncreases: ReferralCreditIncreaseDto[];
}) {
  const totalCredits = creditIncreases.reduce(
    (acc, curr) => acc + curr.credits,
    0,
  );

  return (
    <>
      <div className="flex flex-1 flex-col gap-3 self-stretch overflow-y-auto px-4 py-4 md:px-6">
        {creditIncreases.map((creditIncrease) => (
          <EarnedCreditsItem
            key={creditIncrease.externalId}
            creditIncrease={creditIncrease}
          />
        ))}
      </div>
      <div className="flex shrink-0 basis-auto items-center border-t border-border py-4 pr-10 pl-8 md:pr-12 md:pl-10">
        <p className="flex-1 text-sm font-medium text-text-primary">
          Total earned credits
        </p>
        <CreditsAmount credits={totalCredits} />
      </div>
    </>
  );
}

function EarnedCreditsItem({
  creditIncrease,
}: {
  creditIncrease: ReferralCreditIncreaseDto;
}) {
  return (
    <div className="flex shrink-0 basis-auto items-center overflow-hidden rounded-xl border border-border py-3 pr-6 pl-4">
      <IconGift className="h-5 w-5 shrink-0 text-tertiary-400" />
      <div className="mx-3 w-full flex-1 overflow-hidden">
        <p className="text-sm font-semibold">Friend made a purchase!</p>
        <p className="text-sm text-text-secondary">
          {formatCreatedAt(creditIncrease.createdAt, "Earned")}
        </p>
      </div>
      <CreditsAmount credits={creditIncrease.credits} />
    </div>
  );
}

function CreditsAmount({ credits }: { credits: number }) {
  return (
    <div className="flex items-center justify-center">
      <img src="/CreditIcon.svg" alt="" className="mr-1.5 mb-0.75 h-5 w-5" />
      <span className="font-semibold">{credits.toLocaleString("en-US")}</span>
    </div>
  );
}
